import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  User, Edit3, Camera, Save, X, LogOut,
  MapPin, Calendar, Globe, Shield, Users, Swords,
  RefreshCw, CheckCircle, AlertCircle, Zap, UserPlus,
  UserMinus, ChevronRight, Star
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const BACKEND = 'https://backproject.meanc.net/meanc';
const PRIMARY = '#0EA5E9';
const ACCENT = '#38BDF8';

const getPhoto = (u) => {
  if (!u) return null;
  const raw = u.profile_picture || u.photo || u.avatar || u.picture;
  if (!raw) return null;
  if (raw.startsWith('http')) return raw;
  return `${BACKEND}${raw}`;
};

const fmt = (n) => {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

const hashColor = (str = '') => {
  const palette = ['#0EA5E9','#38BDF8','#0EA5E9','#8B5CF6','#EC4899','#F97316','#22C55E','#14B8A6'];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return palette[Math.abs(h) % palette.length];
};

const EDITABLE_FIELDS = ['first_name', 'last_name', 'username', 'bio', 'country', 'hometown', 'gender', 'date_of_birth'];
const GENDER_OPTIONS = [{ label: '— Select —', value: '' }, { label: 'Male', value: 'M' }, { label: 'Female', value: 'F' }, { label: 'Other', value: 'O' }];

// ── Stat card ────────────────────────────────────────────────
function StatCard({ icon: Icon, value, label, color, sub }) {
  return (
    <div className="pf-stat-card">
      <div className="pf-stat-icon" style={{ background: `${color}18`, color }}>
        <Icon size={16} />
      </div>
      <div className="pf-stat-body">
        <span className="pf-stat-value" style={{ color }}>{fmt(value)}</span>
        <span className="pf-stat-label">{label}</span>
        {sub && <span className="pf-stat-sub">{sub}</span>}
      </div>
    </div>
  );
}

// ── Follow user row ──────────────────────────────────────────
function UserRow({ person, isFollowing, isLoading, onToggle, isSelf }) {
  const name = person.username || person.user?.username || 'Fan';
  const photo = getPhoto(person);
  const color = hashColor(name);
  return (
    <div className="pf-user-row">
      <div className="pf-user-avatar" style={{ background: photo ? 'transparent' : color }}>
        {photo ? <img src={photo} alt={name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : name[0]?.toUpperCase()}
      </div>
      <div className="pf-user-info">
        <span className="pf-user-name">{name}</span>
        {person.bio && <span className="pf-user-bio">{person.bio}</span>}
      </div>
      {!isSelf && (
        <button
          className={`pf-follow-chip ${isFollowing ? 'following' : ''}`}
          onClick={() => onToggle(person)}
          disabled={isLoading}
        >
          {isLoading ? '…' : isFollowing ? <><UserMinus size={12} /> Unfollow</> : <><UserPlus size={12} /> Follow</>}
        </button>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function ProfilePage() {
  const { user, token, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(user);
  const [credits, setCredits] = useState(null);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [myFollowIds, setMyFollowIds] = useState(new Set());
  const [followLoading, setFollowLoading] = useState({});
  const [activeTab, setActiveTab] = useState('about');
  const [editOpen, setEditOpen] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const fileInputRef = useRef(null);

  // ── Initial load ───────────────────────────────────────────
  const loadAll = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setRefreshing(true);
    try {
      const profileData = await api.getProfile(token);
      const p = Array.isArray(profileData) ? profileData[0] : profileData;
      setProfile(p);
      await refreshUser();

      if (p?.id) {
        const [cred, fwrs, fwing, myFws] = await Promise.all([
          api.getCredits(token).catch(() => null),
          api.getUserFollowers(token, p.id).catch(() => []),
          api.getUserFollowing(token, p.id).catch(() => []),
          api.getMyFollows(token).catch(() => []),
        ]);
        if (cred) setCredits(cred);
        setFollowers(Array.isArray(fwrs) ? fwrs : fwrs?.results ?? []);
        setFollowing(Array.isArray(fwing) ? fwing : fwing?.results ?? []);
        const fwList = Array.isArray(myFws) ? myFws : myFws?.results ?? [];
        const myId = p.id;
        const ids = new Set(
          fwList.filter(e => (e?.follower?.id ?? e?.follower) === myId && e.status === 'accepted')
            .map(e => e?.followed?.id ?? e?.followed)
        );
        setMyFollowIds(ids);
      }
    } catch {}
    finally { setRefreshing(false); }
  }, [token, refreshUser]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Follow toggle ──────────────────────────────────────────
  const handleFollowToggle = async (person) => {
    const id = person.id;
    if (!token || followLoading[id]) return;
    setFollowLoading(p => ({ ...p, [id]: true }));
    try {
      if (myFollowIds.has(id)) {
        await api.unfollowUser(token, id);
        setMyFollowIds(prev => { const s = new Set(prev); s.delete(id); return s; });
      } else {
        await api.followUser(token, id);
        setMyFollowIds(prev => new Set([...prev, id]));
      }
    } catch {}
    setFollowLoading(p => ({ ...p, [id]: false }));
  };

  // ── Edit ───────────────────────────────────────────────────
  const openEdit = () => {
    if (!profile) return;
    const f = {};
    EDITABLE_FIELDS.forEach(k => { f[k] = profile[k] || ''; });
    setEditFields(f); setSaveError(''); setSaveSuccess(false); setEditOpen(true);
  };

  const handleSave = async () => {
    const payload = {};
    EDITABLE_FIELDS.forEach(k => {
      const curr = profile[k] ?? ''; const next = editFields[k] ?? '';
      if (next !== curr) payload[k] = next || null;
    });
    if (!Object.keys(payload).length) { setEditOpen(false); return; }
    setSaving(true); setSaveError('');
    try {
      const updated = await api.updateProfile(token, payload);
      setProfile(prev => ({ ...prev, ...updated }));
      await refreshUser();
      const chatPayload = {};
      ['bio', 'country', 'date_of_birth'].forEach(k => { if (payload[k] !== undefined) chatPayload[k] = payload[k]; });
      if (Object.keys(chatPayload).length) api.updateChatProfile(token, chatPayload).catch(() => {});
      setSaveSuccess(true);
      setTimeout(() => { setSaveSuccess(false); setEditOpen(false); }, 1200);
    } catch (err) { setSaveError(err.message || 'Update failed.'); }
    finally { setSaving(false); }
  };

  // ── Photo upload ───────────────────────────────────────────
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('profile_picture', file);
      const res = await fetch(`${BACKEND}/users/me/profile/`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error('Upload failed');
      const updated = await res.json();
      setProfile(prev => ({ ...prev, ...updated }));
      await refreshUser();
    } catch (err) { alert(err.message || 'Photo upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  if (!profile) return (
    <div className="profile-page">
      <div className="feed-empty">
        <User size={48} color="rgba(14,165,233,0.4)" />
        <p>Loading profile…</p>
      </div>
    </div>
  );

  const photo = getPhoto(profile);
  const displayName = profile.username || profile.first_name || profile.email?.split('@')[0] || 'You';
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
  const followerCount = followers.length || profile.followers_count || profile.follower_count || 0;
  const followingCount = following.length || profile.following_count || profile.followed_count || 0;
  const battles = profile.battles_count ?? profile.rivalry_count ?? 0;
  const creditBalance = credits?.balance ?? credits?.credits ?? null;
  const avatarColor = hashColor(displayName);

  const TABS = [
    { id: 'about', label: 'About' },
    { id: 'followers', label: 'Followers', count: followerCount },
    { id: 'following', label: 'Following', count: followingCount },
  ];

  return (
    <div className="profile-page">

      {/* ── Cover ── */}
      <div className="pf-cover">
        <div className="pf-cover-gradient" />
        <div className="pf-cover-mesh" />
        <div className="pf-cover-orbs">
          <div className="pf-orb pf-orb-1" />
          <div className="pf-orb pf-orb-2" />
        </div>
      </div>

      {/* ── Main card ── */}
      <div className="pf-card">

        {/* Top row: avatar + actions */}
        <div className="pf-top-row">
          <div className="pf-avatar-wrap">
            <div className="pf-avatar" style={{ borderColor: PRIMARY, boxShadow: `0 0 0 4px ${PRIMARY}28` }}>
              {photo ? (
                <img src={photo} alt={displayName}
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              ) : null}
              <div className="pf-avatar-fallback" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, display: photo ? 'none' : 'flex' }}>
                {displayName[0]?.toUpperCase()}
              </div>
            </div>
            <button className="pf-camera-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading} title="Change photo">
              {uploading ? <div className="auth-spinner" style={{ width: 14, height: 14 }} /> : <Camera size={14} />}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
          </div>

          <div className="pf-actions-row">
            <button className="pf-icon-btn" onClick={() => loadAll()} disabled={refreshing} title="Refresh">
              <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
            </button>
            <button className="pf-edit-btn" onClick={openEdit}>
              <Edit3 size={15} /> Edit
            </button>
            <button className="pf-logout-btn" onClick={handleLogout} title="Sign out">
              <LogOut size={16} />
            </button>
          </div>
        </div>

        {/* Name + handle + bio */}
        <div className="pf-identity">
          <h2 className="pf-name">{fullName || displayName}</h2>
          {profile.username && <p className="pf-handle">@{profile.username}</p>}
          {profile.bio && <p className="pf-bio">{profile.bio}</p>}
          <div className="pf-meta-row">
            {profile.country && <span className="pf-meta-chip"><MapPin size={12} />{profile.country}</span>}
            {profile.hometown && <span className="pf-meta-chip"><Globe size={12} />{profile.hometown}</span>}
            {profile.date_of_birth && <span className="pf-meta-chip"><Calendar size={12} />{new Date(profile.date_of_birth).getFullYear()}</span>}
            {profile.user_type && <span className="pf-meta-chip"><Shield size={12} />{profile.user_type}</span>}
          </div>
        </div>

        {/* Stats row */}
        <div className="pf-stats-row">
          <StatCard icon={Users} value={followerCount} label="Followers" color={PRIMARY} />
          <StatCard icon={UserPlus} value={followingCount} label="Following" color={ACCENT} />
          <StatCard icon={Swords} value={battles} label="Battles" color="#F97316" />
          {creditBalance !== null && (
            <StatCard icon={Zap} value={creditBalance} label="Credits" color="#22C55E" sub={credits?.max_credits ? `/ ${credits.max_credits}` : undefined} />
          )}
        </div>

        {/* Credits bar if available */}
        {creditBalance !== null && credits?.max_credits && (
          <div className="pf-credits-bar-wrap">
            <div className="pf-credits-bar">
              <div className="pf-credits-fill" style={{ width: `${Math.min(100, (creditBalance / credits.max_credits) * 100)}%` }} />
            </div>
            <span className="pf-credits-label"><Zap size={11} color="#22C55E" /> {creditBalance} / {credits.max_credits} credits</span>
          </div>
        )}

        {/* Tabs */}
        <div className="pf-tabs">
          {TABS.map(t => (
            <button key={t.id} className={`pf-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
              {t.label}
              {t.count > 0 && <span className="pf-tab-count">{fmt(t.count)}</span>}
            </button>
          ))}
        </div>

        {/* ── About tab ── */}
        {activeTab === 'about' && (
          <div className="pf-tab-content">
            <div className="pf-section">
              <span className="pf-section-label">Account</span>
              <div className="pf-detail-list">
                {[
                  { label: 'Email', value: profile.email },
                  { label: 'Account type', value: profile.user_type || 'Fan' },
                  { label: 'Member since', value: profile.date_joined || profile.created_at ? new Date(profile.date_joined || profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : null },
                  { label: 'Gender', value: profile.gender === 'M' ? 'Male' : profile.gender === 'F' ? 'Female' : profile.gender === 'O' ? 'Other' : null },
                ].filter(r => r.value).map(r => (
                  <div key={r.label} className="pf-detail-row">
                    <span className="pf-detail-label">{r.label}</span>
                    <span className="pf-detail-value">{r.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Followers tab ── */}
        {activeTab === 'followers' && (
          <div className="pf-tab-content">
            {followers.length === 0 ? (
              <div className="feed-empty" style={{ padding: '32px 0' }}>
                <Users size={40} color="rgba(14,165,233,0.3)" />
                <p>No followers yet</p>
                <span>Share your profile to grow your audience.</span>
              </div>
            ) : followers.map((p, i) => (
              <UserRow
                key={p.id || i}
                person={p}
                isFollowing={myFollowIds.has(p.id)}
                isLoading={!!followLoading[p.id]}
                onToggle={handleFollowToggle}
                isSelf={p.id === profile.id}
              />
            ))}
          </div>
        )}

        {/* ── Following tab ── */}
        {activeTab === 'following' && (
          <div className="pf-tab-content">
            {following.length === 0 ? (
              <div className="feed-empty" style={{ padding: '32px 0' }}>
                <UserPlus size={40} color="rgba(14,165,233,0.3)" />
                <p>Not following anyone yet</p>
                <span>Follow fans from the Teams page.</span>
              </div>
            ) : following.map((p, i) => (
              <UserRow
                key={p.id || i}
                person={p}
                isFollowing={myFollowIds.has(p.id)}
                isLoading={!!followLoading[p.id]}
                onToggle={handleFollowToggle}
                isSelf={p.id === profile.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Edit modal ── */}
      {editOpen && (
        <div className="modal-overlay" onClick={() => setEditOpen(false)}>
          <div className="pf-edit-modal" onClick={e => e.stopPropagation()}>
            <div className="pf-edit-modal-header">
              <h3>Edit Profile</h3>
              <button className="modal-close-btn" onClick={() => setEditOpen(false)}><X size={18} /></button>
            </div>

            <div className="pf-edit-modal-body">
              <p className="pf-edit-section-label">Identity</p>
              {['first_name', 'last_name', 'username'].map(f => (
                <div key={f} className="pf-edit-field">
                  <label>{f.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</label>
                  <input value={editFields[f] || ''} onChange={e => setEditFields(p => ({ ...p, [f]: e.target.value }))} />
                </div>
              ))}

              <p className="pf-edit-section-label">About</p>
              <div className="pf-edit-field">
                <label>Bio</label>
                <textarea rows={3} placeholder="Tell the community about yourself"
                  value={editFields.bio || ''} onChange={e => setEditFields(p => ({ ...p, bio: e.target.value }))} />
              </div>
              <div className="pf-edit-field">
                <label>Date of birth</label>
                <input type="date" value={editFields.date_of_birth || ''}
                  onChange={e => setEditFields(p => ({ ...p, date_of_birth: e.target.value }))} />
              </div>
              <div className="pf-edit-field">
                <label>Gender</label>
                <select value={editFields.gender || ''} onChange={e => setEditFields(p => ({ ...p, gender: e.target.value }))}>
                  {GENDER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <p className="pf-edit-section-label">Location</p>
              {['country', 'hometown'].map(f => (
                <div key={f} className="pf-edit-field">
                  <label>{f.replace(/\b\w/g, c => c.toUpperCase())}</label>
                  <input value={editFields[f] || ''} onChange={e => setEditFields(p => ({ ...p, [f]: e.target.value }))} />
                </div>
              ))}

              {saveError && <div className="pf-edit-error"><AlertCircle size={14} />{saveError}</div>}
              {saveSuccess && <div className="pf-edit-success"><CheckCircle size={14} />Profile updated!</div>}
            </div>

            <div className="pf-edit-modal-footer">
              <button className="pf-cancel-btn" onClick={() => setEditOpen(false)}>Cancel</button>
              <button className="pf-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? <span className="auth-spinner" /> : <><Save size={15} /> Save changes</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
